import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, usePermissions } from '@hooks';
import Drawer from '@components/Drawer';
import Card from '@components/Card';
import { Typography } from '@components/Typography';
import { XIcon } from 'assets/images/integrations';
import { AccIntegrationName } from '@constants/integrations';
import { getActiveIntegrations } from '@helpers/integrations';
import { handleApiError } from '@helpers/api';
import { alertActions, accountActions, locationsProfilesActions } from '@actions';
import { xIntegrationService } from '@services';
import { RoutePath } from '@constants/routes';

// Drawer shown before a Location is wired to an X profile. Previews the X account
// (handle / display name / avatar) so the user sees exactly which identity this Location
// will publish to. Mirrors IFoodIntegration's UX — one tappable card, one Connect action.
const XIntegration = ({ onClose, onConnect, isOpen, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const [accountInfo, setAccountInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const account = useSelector(state => state.account.account);
    const { hasFullSettings } = usePermissions();
    const canManageIntegrations = hasFullSettings();

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const xIntegrations = useMemo(() => getActiveIntegrations(account, AccIntegrationName.X), [account]);
    const X_CONNECTED = xIntegrations.length > 0;

    const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);

    useEffect(() => {
        if (isOpen && X_CONNECTED) {
            setSelectedIntegrationId(prev =>
                prev && xIntegrations.some(i => i.id === prev) ? prev : xIntegrations[0]?.id,
            );
        }
    }, [isOpen, X_CONNECTED, xIntegrations]);

    useEffect(() => {
        // Fetch the snapshot for the selected account so the user sees exactly which identity will
        // publish; only meaningful once an account-level integration is active and chosen.
        if (!isOpen || !selectedIntegrationId) return;
        setIsLoading(true);
        setAccountInfo(null);
        xIntegrationService
            .getAccountInfo(selectedIntegrationId)
            .then(setAccountInfo)
            .catch(() => setAccountInfo(null))
            .finally(() => setIsLoading(false));
    }, [isOpen, selectedIntegrationId]);

    const goToIntegrations = () => navigate(RoutePath.AccountSettingsIntegrations);

    const onConnectX = async () => {
        try {
            onClose();
            const updatedCompany = await xIntegrationService.connectLocation(
                selectedCompany?.companyId,
                selectedIntegrationId,
            );
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (!X_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        }
        if (X_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: onConnectX,
                primaryText: t('buttons.connect'),
                primaryDisabled: !selectedIntegrationId || isLoading,
            };
        }
        return {
            primaryAction: goToIntegrations,
            primaryText: t('SettingsCompanies.goToIntegrations'),
            primaryDisabled: true,
        };
    };

    return (
        <Drawer
            aboveModal
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                X_CONNECTED ? t('SettingsCompanies.connectLocation') : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                X_CONNECTED
                    ? t('SettingsCompanies.connectDrawerDescription')
                    : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
        >
            <div style={{ display: 'grid', gridGap: '8px' }}>
                {X_CONNECTED &&
                    xIntegrations.map(integration => {
                        const isSelected = selectedIntegrationId === integration.id;
                        const preview = isSelected ? accountInfo : null;
                        const label =
                            preview?.displayName ||
                            preview?.handle ||
                            integration.accountLabel ||
                            integration.userIdentifier ||
                            'X';
                        return (
                            <Card
                                key={integration.id}
                                type="radio"
                                selected={isSelected}
                                title={label}
                                value={integration.id}
                                setValue={() => setSelectedIntegrationId(integration.id)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {preview?.profileImageUrl ? (
                                        <img
                                            src={preview.profileImageUrl}
                                            alt=""
                                            width={24}
                                            height={24}
                                            style={{ borderRadius: '50%' }}
                                        />
                                    ) : (
                                        <XIcon width={20} height={20} />
                                    )}
                                    {(preview?.handle || integration.accountLabel) && (
                                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                            {preview?.handle ? `@${preview.handle}` : integration.accountLabel}
                                        </Typography>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
            </div>
        </Drawer>
    );
};

export default XIntegration;
